import { useMutation, useQueryClient } from '@tanstack/react-query'

import { USER_GROUP_QUERY_KEYS } from '../api/endpoints'
import type { UserGroupApi } from '../api/userGroupApi'
import type {
  UserGroup,
  UserGroupCreate,
  UserGroupUpdate,
} from '../types/userGroup.types'

// ─── Error types ──────────────────────────────────────────────────────────────

export type UserGroupErrorCode =
  'not_found' | 'forbidden' | 'validation' | 'unknown'

export class UserGroupError extends Error {
  constructor(
    public readonly code: UserGroupErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'UserGroupError'
  }
}

function extractValidationMessage(errorData: unknown): string {
  if (!errorData || typeof errorData !== 'object') return 'Validation failed'

  const data = errorData as Record<string, unknown>

  if (typeof data.detail === 'string') return data.detail

  const messages: string[] = []
  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value)) {
      for (const msg of value) {
        if (typeof msg === 'string') {
          messages.push(key === 'non_field_errors' ? msg : `${key}: ${msg}`)
        }
      }
    } else if (typeof value === 'string') {
      messages.push(`${key}: ${value}`)
    }
  }

  return messages.length > 0 ? messages.join('; ') : 'Validation failed'
}

// ─── Create ───────────────────────────────────────────────────────────────────

export const useCreateUserGroup = ({
  api,
  pbxId,
}: {
  api: UserGroupApi
  pbxId: number
}) => {
  const queryClient = useQueryClient()

  return useMutation<UserGroup | null, UserGroupError, UserGroupCreate>({
    mutationFn: async (data) => {
      const response = await api.createUserGroup(pbxId, data)
      if (response.ok) return response.data ?? null

      if (response.status === 400) {
        throw new UserGroupError(
          'validation',
          extractValidationMessage(response.data),
        )
      }
      if (response.status === 403) {
        throw new UserGroupError(
          'forbidden',
          'You do not have permission to perform this action',
        )
      }
      throw new UserGroupError('unknown', 'Failed to create user group')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_GROUP_QUERY_KEYS.lists() })
    },
  })
}

// ─── Update ───────────────────────────────────────────────────────────────────

export interface UpdateUserGroupVariables {
  id: number
  data: UserGroupUpdate
}

export const useUpdateUserGroup = ({
  api,
  pbxId,
}: {
  api: UserGroupApi
  pbxId: number
}) => {
  const queryClient = useQueryClient()

  return useMutation<
    UserGroup | null,
    UserGroupError,
    UpdateUserGroupVariables
  >({
    mutationFn: async ({ id, data }) => {
      const response = await api.updateUserGroup(pbxId, id, data)
      if (response.ok) return response.data ?? null

      if (response.status === 400) {
        throw new UserGroupError(
          'validation',
          extractValidationMessage(response.data),
        )
      }
      if (response.status === 403) {
        throw new UserGroupError(
          'forbidden',
          'You do not have permission to perform this action',
        )
      }
      if (response.status === 404) {
        throw new UserGroupError('not_found', 'User group not found')
      }
      throw new UserGroupError('unknown', 'Failed to update user group')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_GROUP_QUERY_KEYS.lists() })
      queryClient.invalidateQueries({
        queryKey: USER_GROUP_QUERY_KEYS.details(),
      })
    },
  })
}

// ─── Delete ───────────────────────────────────────────────────────────────────

export interface DeleteUserGroupVariables {
  id: number
}

export const useDeleteUserGroup = ({
  api,
  pbxId,
}: {
  api: UserGroupApi
  pbxId: number
}) => {
  const queryClient = useQueryClient()

  return useMutation<void, UserGroupError, DeleteUserGroupVariables>({
    mutationFn: async ({ id }) => {
      const response = await api.deleteUserGroup(pbxId, id)
      if (response.ok) return

      if (response.status === 404) {
        throw new UserGroupError('not_found', 'User group not found')
      }
      if (response.status === 403) {
        throw new UserGroupError(
          'forbidden',
          'You do not have permission to perform this action',
        )
      }
      throw new UserGroupError('unknown', 'Failed to delete user group')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_GROUP_QUERY_KEYS.lists() })
    },
  })
}
